import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { CoreAppState } from '../../state/core-app.state';
import { selectMeetingTypeState } from '../../state/meeting-type/meeting-type.reducer';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { meetingTypeActions } from '../../state/meeting-type/meeting-type.actions';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  MeetingAvailabilityResourceTypeMappings,
  MeetingTypeResourceTypeMappings,
} from '@aa/nest/resource';
import { scheduledMeetingActions } from '../../state/scheduled-meeting/scheduled-meeting.actions';
import { getDateTimeFromOther } from 'src/libs/ts/common/src/lib/date-time/get-date-time-from-other';
import { firstValueFrom } from 'rxjs';
import { selectAuth } from '../../state/auth/auth.reducer';
import { selectMeetingAvailabilityState } from '../../state/meeting-availability/meeting-availability.reducer';
import { meetingAvailabilityActions } from '../../state/meeting-availability/meeting-availability.actions';
import { luxonWeekdayToWeekday, weekdayToLuxonWeekday } from '@aa/ts/common';
import { MatSelectModule } from '@angular/material/select';
import { selectScheduledMeetingState } from '../../state/scheduled-meeting/scheduled-meeting.reducer';
import { ScheduledMeeting } from '@prisma/client';
import { DateTime } from 'luxon';
import { Router } from '@angular/router';

@Component({
  selector: 'aa-schedule-meeting-view',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatButtonModule,
    MatInputModule,
    MatNativeDateModule,
    MatDatepickerModule,
    MatSelectModule,
  ],
  templateUrl: './schedule-meeting-view.component.html',
  styleUrl: './schedule-meeting-view.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScheduleMeetingViewComponent {
  private readonly store = inject(Store<CoreAppState>);
  private readonly router = inject(Router);

  selectedMeetingType = signal<
    MeetingTypeResourceTypeMappings['resourceT'] | undefined
  >(undefined);

  dateFormControl = new FormControl();
  timeFormControl = new FormControl<string | undefined>(undefined);

  meetingTypes$ = this.store
    .select((s) => selectMeetingTypeState(s).items)
    .pipe(takeUntilDestroyed());
  meetingAvailabilities$ = this.store
    .select((s) => selectMeetingAvailabilityState(s).items)
    .pipe(takeUntilDestroyed());
  scheduledMeetings$ = this.store
    .select((s) => selectScheduledMeetingState(s).items)
    .pipe(takeUntilDestroyed());

  ngOnInit() {
    this.store.dispatch(meetingTypeActions.loadItems({}));
    this.store.dispatch(
      meetingAvailabilityActions.loadItems({
        query: {
          orderBy: {
            weekday: 'asc',
          },
        },
      }),
    );
    this.store.dispatch(scheduledMeetingActions.loadItems({}));
  }

  updateMeetingTypeValue(
    meetingType: MeetingTypeResourceTypeMappings['resourceWithRelationsT'],
  ) {
    this.selectedMeetingType.set(meetingType);
  }

  mapMeetingAvailabilitiesToWeekdayOptions(
    meetingAvailabilities: MeetingAvailabilityResourceTypeMappings['resourceT'][],
  ) {
    let options: { label: string; value: any }[] = [];
    for (let availability of meetingAvailabilities) {
      const luxonWeekDay = weekdayToLuxonWeekday[availability.weekday];
      const today = DateTime.now();
      const daysUntilGoalWeekday = (luxonWeekDay - today.weekday + 7) % 7;
      for (let week = 0; week < 3; week++) {
        const dateOption = today.plus({
          days: daysUntilGoalWeekday + 7 * week,
        });
        if (dateOption.diffNow().as('days') < 3) continue;

        const newOption = {
          label: dateOption.toFormat('ccc LLL dd '),
          value: dateOption,
        };

        if (!options.find((o) => o.value == newOption.value)) {
          options.push(newOption);
        }
      }
    }

    return options.sort((a, b) => {
      const aDate = a.value as DateTime;
      const bDate = b.value as DateTime;

      return aDate.diff(bDate).as('days');
    });
  }

  getTimeOptions(
    date: DateTime | undefined | null,
    meetingAvailabilities: MeetingAvailabilityResourceTypeMappings['resourceT'][],
    scheduledMeetings: ScheduledMeeting[],
  ) {
    if (!date) return [];
    const weekday = luxonWeekdayToWeekday[date.weekday];

    let options: { label: string; value: any }[] = [];
    for (const availability of meetingAvailabilities) {
      if (availability.weekday == weekday) {
        const startSplit = availability.startTime.split(':');
        const endSplit = availability.endTime.split(':');

        let startHour = +startSplit[0];
        if (availability.startTime.includes('PM')) {
          startHour += 12;
        }
        const startMin = +startSplit[1].split(' ')[0];

        let endHour = +endSplit[0];
        if (availability.endTime.includes('PM')) {
          endHour += 12;
        }
        const endMin = +endSplit[1].split(' ')[0];

        let hour = startHour;
        let minute = startMin;

        while (hour < endHour + 1) {
          const hourAs12 = hour > 12 ? hour - 12 : hour;
          const newTime = `${hourAs12}:${minute.toString().padStart(2, '0')} ${
            hour > 12 ? 'PM' : 'AM'
          }`;

          if (!options.find((o) => o.value == newTime)) {
            // confirm there are no other meetings that this would overlap with

            const newStartTimeAsDT = date
              .set({
                hour: hour,
                minute: minute,
              })
              .setZone('America/Chicago');
            const newEndTimeAsDt = newStartTimeAsDT
              .plus({
                minutes: this.selectedMeetingType()?.durationInMinutes ?? 0,
              })
              .setZone('America/Chicago');

            const foundMeeting = scheduledMeetings.find((m) => {
              const startDT = getDateTimeFromOther(m.startAt)!;
              const endDT = getDateTimeFromOther(m.endAt)!;

              // console.log(
              //   'SEARCH FOR MEETING OVERLAPPING WITH: ',
              //   formatDateTime(newStartTimeAsDT),
              //   formatDateTime(newEndTimeAsDt),
              //   'CHECKING: ',
              //   formatDateTime(startDT),
              //   formatDateTime(endDT),
              // );

              if (
                (newStartTimeAsDT.diff(startDT).as('minutes') >= 0 &&
                  newStartTimeAsDT.diff(endDT).as('minutes') <= 0) ||
                (newEndTimeAsDt.diff(startDT).as('minutes') >= 0 &&
                  newEndTimeAsDt.diff(endDT).as('minutes') <= 0)
              ) {
                return true;
              }
              return false;
            });
            if (!foundMeeting) {
              options.push({
                label: newTime,
                value: newTime,
              });
            }
          }

          minute += 5;
          if (minute > 55) {
            minute = 0;
            hour += 1;
          }
        }
      }
    }
    return options;
  }

  isValid() {
    return (
      !!this.dateFormControl.value &&
      this.selectedMeetingType()!! &&
      this.timeFormControl.value
    );
  }

  async submit() {
    if (this.isValid()) {
      const authenticatedCustomer = await firstValueFrom(
        this.store.select((s) => selectAuth(s).user?.customerProfileId),
      );

      let startAt = getDateTimeFromOther(this.dateFormControl.value)!;
      const time = this.timeFormControl.value as string;
      const timeSplit = time.split(':');

      const hour = time.includes('PM') ? +timeSplit[0] + 12 : +timeSplit[0];
      const minutes = +timeSplit[1].split(' ')[0];

      startAt = startAt
        .set({
          hour,
          minute: minutes,
        })
        .setZone('America/Chicago');

      this.store.dispatch(
        scheduledMeetingActions.createItem({
          data: {
            startAt: startAt.toJSDate(),
            endAt: startAt
              .plus({
                minutes: this.selectedMeetingType()!.durationInMinutes,
              })
              .toJSDate(),
            meetingTypeId: this.selectedMeetingType()!.id,
            customerProfileId: authenticatedCustomer!,
          },
        }),
      );

      this.router.navigateByUrl('/');
    }
  }
}
